import { IS_DEV, MACHINE_TYPES } from '../../shared/constants';
import { VendingMachineTypeId } from '../../shared/types';

export const PAYMENT_PAGES = {
    select: 'select',
    snack: 'snack',
    water: 'water',
    pulse: 'pulse',
    payment: 'payment',
    paymentCT5: 'payment5',
    paymentCT10: 'payment10',
    carWash: 'car-wash',
    wait: 'wait',
    popcorn: 'payment-popcorn',
} as const;

export const PAYMENT_ROUTES = {
    select: `/:id/${PAYMENT_PAGES.select}`,
    snack: `/:id/${PAYMENT_PAGES.snack}`,
    water: `/:id/${PAYMENT_PAGES.water}`,
    pulse: `/:id/${PAYMENT_PAGES.pulse}`,
    payment: `/:id/${PAYMENT_PAGES.payment}`,
    paymentCT5: `/:id/${PAYMENT_PAGES.paymentCT5}`,
    paymentCT10: `/:id/${PAYMENT_PAGES.paymentCT10}`,
    carWash: `/:id/${PAYMENT_PAGES.carWash}`,
    wait: `/:id/${PAYMENT_PAGES.wait}`,
    popcorn: `/:id/${PAYMENT_PAGES.popcorn}`,
} as const;

/**
 * Which payment screen a machine type opens after connecting.
 *
 * Derived from PAYMENT_ROUTES rather than repeating the slugs. It used to live in
 * `shared/constants.ts` as MACHINE_PATHS, holding the same ten slug literals as
 * PAYMENT_PAGES with no type link between the two declarations — they matched by
 * luck, not by construction. It also belongs here rather than app-wide: a
 * machine-type → payment-screen map is payment-domain knowledge.
 *
 * Every one of the 16 machine types is listed, so `tsc` catches a new type that
 * nobody routed.
 */
export const MACHINE_ROUTES: Record<VendingMachineTypeId, string> = {
    [MACHINE_TYPES.FRIDGE]: PAYMENT_ROUTES.paymentCT10,
    [MACHINE_TYPES.SNACK_MACHINE_PULS]: PAYMENT_ROUTES.snack,
    [MACHINE_TYPES.WATER_MACHINE_PULS]: PAYMENT_ROUTES.water,
    [MACHINE_TYPES.PULSE_MACHINE]: PAYMENT_ROUTES.pulse,
    [MACHINE_TYPES.CAR_WASH_MACHINE]: PAYMENT_ROUTES.carWash,
    [MACHINE_TYPES.POPCORN_PULSE_MACHINE]: PAYMENT_ROUTES.popcorn,
    [MACHINE_TYPES.SNACK_MACHINE]: PAYMENT_ROUTES.select,
    [MACHINE_TYPES.COFFEE_AUTOMAT]: PAYMENT_ROUTES.select,
    [MACHINE_TYPES.SNACK_MACHINE_SPARKA]: PAYMENT_ROUTES.select,
    [MACHINE_TYPES.COFFEE_MACHINE_SPARKA]: PAYMENT_ROUTES.select,
    [MACHINE_TYPES.COFFEE_MACHINE]: PAYMENT_ROUTES.select,
    [MACHINE_TYPES.FRESH_MACHINE]: PAYMENT_ROUTES.select,
    [MACHINE_TYPES.ICE_CREAM_MACHINE]: PAYMENT_ROUTES.select,
    [MACHINE_TYPES.CIGARETTE_MACHINE]: PAYMENT_ROUTES.select,
    [MACHINE_TYPES.CONDOM_MACHINE]: PAYMENT_ROUTES.select,
    [MACHINE_TYPES.FRIDGE_SMART]: PAYMENT_ROUTES.select,
};

export const LIMITS = {
    SNACK: { MIN: 0.1 },
    WATER: { MIN: 1 },
    PULSE: { MIN: 1 },
    PAYMENT_CT5: { MIN: 0.01 },
    PAYMENT_CT10: { MIN: 0.01 },
    CAR_WASH: { MIN: 1 },
} as const;

/**
 * Mirrors the backend fallback in check.service.js / connect.service.js:
 *   CASE WHEN vm.max_credit_app > 0 THEN ... WHEN company.max_credit_app > 0 THEN ... ELSE 500 END
 * Used as defensive fallback if the API ever returns a falsy max_credit_app.
 */
export const DEFAULT_WEB_PAYMENT_MAX = 500;

export const WAIT_TIMER = IS_DEV ? 40 : 120;

export const MERCHANT_NAME = 'Prostopay LLC';

/** Delay before navigating to /wait — prevents component unmount before payment request completes */
export const PAYMENT_NAVIGATION_DELAY = 100;
/**
 * How long the «confirm with your bank» notice stays before the 3DS page opens
 * (SWRN-2094). Short on purpose: the machine closes the session ≈ 75–90 s after
 * the commit, and the buyer still has to pass the bank's challenge in that window.
 */
export const THREE_DS_REDIRECT_DELAY = 1500;

/** Apple Pay JS API version (https://developer.apple.com/documentation/apple_pay_on_the_web) */
export const APPLE_PAY_SESSION_VERSION = 3;
export const APPLE_PAY_MERCHANT_CAPABILITIES = ['supports3DS'] as const;
export const APPLE_PAY_SUPPORTED_NETWORKS = ['masterCard', 'visa'] as const;

/** Google Pay card networks and gateway identifiers */
export const GOOGLE_PAY_SUPPORTED_NETWORKS = ['MASTERCARD', 'VISA'] as const;
export const GOOGLE_PAY_AUTH_METHODS = ['PAN_ONLY', 'CRYPTOGRAM_3DS'] as const;
export const GOOGLE_PAY_GATEWAY = 'portmonecom';
export const EMERCHANT_PAY_GATEWAY = 'emerchantpay';
/** EMerchantPay accepts only device-bound tokens (3DS cryptogram) */
export const EMERCHANT_PAY_AUTH_METHODS = ['CRYPTOGRAM_3DS'] as const;
/** Tranzzo (SWRN-1543): gatewayMerchantId is the POS id from merchants.extra */
export const TRANZZO_PAY_GATEWAY = 'tranzzo';

/** MyPOS and EMerchantPay require EUR regardless of VM currency */
export const MYPOS_CURRENCY_CODE = 'EUR';

/** Debounce delay for volume/amount input changes (ms) */
export const VOLUME_INPUT_DEBOUNCE_MS = 500;

/** Debounce delay for keystroke-driven amount submits (CT5/CT10/Snack) — see useOrderAmount({ debounceMs }) */
export const AMOUNT_INPUT_DEBOUNCE_MS = 500;

/** Available play counts for pulse/popcorn machines */
export const AVAILABLE_PULSE_TRIES = [1, 2, 3] as const;
